import { useState, useEffect } from "react"
import classnames from "classnames"
import { useController } from "react-hook-form"

import TextMessage from "../../../TextMessage"

import mergeDeep from "@mobi/utils/object/mergeDeep"

import "./Token.scss"

const Token = ({
  formProps,
  label = "Código de verificação única",
  name = "token",
  required = true,
  size,
  type = "text",
  // TODO: documentar essas props para aparecer certo na interface do storybook
  shouldResetOn = null,
  validators: { rules: propRules = {}, messages: propMessages = {} } = {},
  readOnly,
  disabled = false,
  labels = [],
  onlyNumbers = false,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const [tokenValue, setTokenValue] = useState(Array(size).fill(""))

  const DEFAULT_MESSAGES = {
    required: "campo obrigatório",
    hasSize: "preencha todos os caracteres",
  }

  const DEFAULT_RULES = {
    validate: {
      hasSize: (value) => {
        if (
          disabled ||
          (value && value?.trim()?.replaceAll(" ", "").length >= size)
        )
          return true
        return false
      },
    },
  }

  const rules = mergeDeep(DEFAULT_RULES, propRules, { required })
  const errorMessages = mergeDeep(DEFAULT_MESSAGES, propMessages)

  const { control, resetField } = formProps
  const {
    field,
    fieldState: { invalid, error },
  } = useController({ name, control, rules })

  useEffect(() => {
    if (field.value && shouldResetOn) {
      const fields = document.querySelectorAll(
        `#ds-token-${name} .ds-token__wrapper > input`
      )
      fields.forEach((item) => (item.value = ""))
      setTokenValue([])
      document.activeElement.blur()
      resetField(name)
    }
  }, [shouldResetOn])

  useEffect(() => {
    if (field?.value) {
      setTokenValue(
        field?.value
          ?.split("")
          .map((character) => (character == " " ? "" : character))
      )
    } else {
      cleanFields()
    }
  }, [field.value])

  const dispatchChange = (tokenValue) => {
    field.onChange(
      tokenValue.map((character) => (!character ? " " : character)).join("")
    )
  }

  const handleDigits = (event, index) => {
    if (onlyNumbers) {
      const regex = /\D/g
      if (regex.test(event.target.value)) {
        event.preventDefault()
        return
      }
    }

    const tokenToChange = [...tokenValue]

    if (!event.target.value) {
      tokenToChange[index] = event.target.value
    } else {
      const digits = (event.target.value || "").split("")
      digits.forEach((digit, i) => {
        const digitIndex = index + i
        if (digitIndex >= size) {
          return
        }
        tokenToChange[digitIndex] = digit
      })
    }

      setTokenValue(tokenToChange)

      const isTheLastDigit = index + 1 === size
      const hasValue = event.target.value !== ""

      if (hasValue && !isTheLastDigit) {
        const nextElement = document.getElementById(
          `${name}-digit-${index + 1}`
        )
        nextElement.focus()
      }

      dispatchChange(tokenToChange)
  }

  const handleKeydown = (event, index) => {
    const isBackspace = event.key === "Backspace" || event.key === "Delete"
    const isLeftArrow = event.key === "ArrowLeft"
    const isRightArrow = event.key === "ArrowRight"

    const currentElement = document.getElementById(`${name}-digit-${index}`)
    const previousElement = document.getElementById(
      `${name}-digit-${index - 1}`
    )

    const nextElement = document.getElementById(`${name}-digit-${index + 1}`)

    if (isBackspace) {
      const newTokenValue = [...tokenValue]
      newTokenValue[index] = ""
      setTokenValue(newTokenValue)
      dispatchChange(newTokenValue)
      if (!currentElement.value) {
        previousElement && previousElement.focus()
      }
    }
    if (isLeftArrow) {
      previousElement && previousElement.focus()
    } else if (isRightArrow) {
      nextElement && nextElement.focus()
    }
  }

  const cleanFields = () => {
    setTokenValue(tokenValue.map((_) => ""))
  }

  const handlePaste = (event) => {
    field.onChange(event.clipboardData.getData("Text").substring(0, size))
  }

  let labelsProps = {
    "aria-labelledby": `ds-label-${name}`,
  }

  return (
    <div
      className={classnames("ds-token", {
        [`ds-token--disabled`]: disabled,
        [`ds-token--error`]: invalid,
        [`ds-token--focused`]: isFocused,
        [`ds-token--readOnly`]: readOnly,
      })}
      id={`ds-token-${name}`}
    >
      <label
        id={`ds-label-${name}`}
        className={`ds-token__label`}
        aria-label={label}
        aria-hidden
      >
        {label}
      </label>
      <div className={`ds-token__wrapper`}>
        {Array.apply(null, Array(size)).map((_, index) => {
          if (labels.length > 0) labelsProps = { "aria-label": labels[index] }
          return (
            <input
              className={classnames({
                [`ds-token__digit--large`]: size <= 4,
                [`ds-token__digit`]: size > 4,
              })}
              disabled={disabled}
              id={`${name}-digit-${index}`}
              key={index}
              maxLength={1}
              name={`${name}-digit-${index}`}
              onBlur={(e) => {
                setIsFocused(false)
                field.onBlur(e)
              }}
              onChange={(e) => handleDigits(e, index)}
              // TODO: mandar o foco para o primeiro campo ao focar em qualquer outro caso o valor esteja vazio
              onFocus={() => setIsFocused(true)}
              onKeyDown={(e) => handleKeydown(e, index)}
              onPaste={(e) => handlePaste(e)}
              readOnly={readOnly}
              value={tokenValue[index]}
              type={type}
              {...labelsProps}
              {...props}
            />
          )
        })}
      </div>
      <TextMessage message={errorMessages[error?.type]} show={invalid} />

      <input
        {...field}
        className={`ds-token__hidden-input`}
        id={`${name}-token-controler`}
        name={`${name}-token-controler`}
        aria-hidden={true}
      />
    </div>
  )
}

export default Token
